import { useState } from "react";
import logo from './logo.png';

const T = {
  bg: "#0a0a0a",
  card: "#161616",
  border: "rgba(255,255,255,0.06)",
  red: "#dc2626",
  white: "#ffffff",
  muted: "rgba(255,255,255,0.4)",
  faint: "rgba(255,255,255,0.15)",
  font: "'DM Sans', system-ui, sans-serif",
  serif: "'DM Serif Display', Georgia, serif",
};

const PRIVACY_SECTIONS = [
  {
    title: "Information We Collect",
    content: `When you create a Binos account, we collect your name, work email address, fund name, role, and fund profile details including AUM range, stage focus, and sector focus. We also collect optional details such as your fund website and LinkedIn profile.

When you use Binos, we collect data you input including portfolio company details, investment memos, pitch deck analyses, financial calculations, and notes. We also collect usage data such as features accessed, frequency of use, and session information to improve the product.

When founders submit pitch decks through the Binos founders platform, we collect their name, email, startup details, and the pitch deck PDF file.`,
  },
  {
    title: "How We Use Your Information",
    content: `We use your information solely to provide and improve the Binos platform. Specifically we use it to operate your account and deliver the features you use, to personalise your experience based on your fund profile, to send product updates and important account notifications, and to improve Binos based on aggregate usage patterns.

We do not use your deal flow, portfolio data, investment memos, or pitch deck analyses to train AI models. We do not sell your data to third parties. We do not share your data with other funds or users on the platform.`,
  },
  {
    title: "Data Isolation",
    content: `Every fund on Binos operates in a completely isolated data environment. Your deal flow, portfolio companies, investment memos, thesis, and notes are never visible to other funds or users on the platform.

Row-level security is enforced at the database level, meaning even in the event of an application error, your data cannot be accessed by another fund's account. This is enforced by Supabase's PostgreSQL row-level security policies.`,
  },
  {
    title: "Pitch Deck Storage",
    content: `Pitch decks uploaded to Binos are stored in a private, encrypted storage bucket. No public URLs are generated. Decks are only accessible via signed URLs that expire after one hour and are only generated for authenticated users with explicit access rights.

Founder pitch decks submitted through binos-ai.com/founders are stored in the same private bucket and are only accessible to the Binos team and verified VC funds on the platform. Founders may request deletion of their pitch deck at any time by emailing anas@binos-ai.com and we will action the deletion within 24 hours.`,
  },
  {
    title: "Third Party Services",
    content: `Binos uses the following third party services to operate the platform. Supabase for database, authentication, and file storage — data is stored in the Mumbai (South Asia) region. Vercel for hosting and serverless functions. Google Gemini API for AI-powered pitch deck analysis and investment memo generation — we send pitch deck content to Google's API for processing. We do not send personally identifiable information to Gemini.

We are not responsible for the privacy practices of these third party services and encourage you to review their respective privacy policies.`,
  },
  {
    title: "Data Retention",
    content: `We retain your account data for as long as your account is active. If you request account deletion, we will delete your data within 30 days. Some data may be retained for longer periods where required by law.

Pitch deck analyses and investment memos you create are retained indefinitely as part of your institutional memory — this is a core feature of Binos. You may delete individual items at any time from within the platform.`,
  },
  {
    title: "Your Rights",
    content: `You have the right to access all data we hold about you, to request correction of inaccurate data, to request deletion of your account and associated data, to export your data at any time, and to withdraw consent for data processing.

To exercise any of these rights, email anas@binos-ai.com. We will respond within 72 hours.`,
  },
  {
    title: "Security",
    content: `We implement industry standard security measures including SSL/TLS encryption for all data in transit, AES-256 encryption for data at rest, row-level security enforced at the database level, JWT-based authentication with one hour token expiry, and private storage buckets with no public access.

Despite these measures, no system is completely secure. We encourage you to use a strong password and to notify us immediately at anas@binos-ai.com if you suspect any unauthorised access to your account.`,
  },
  {
    title: "Contact",
    content: `If you have any questions about this privacy policy or how we handle your data, contact us at anas@binos-ai.com. We take privacy seriously and will respond to all inquiries within 72 hours.

This privacy policy was last updated in May 2025. We will notify users of material changes via email.`,
  },
];

const TERMS_SECTIONS = [
  {
    title: "Acceptance of Terms",
    content: `By creating an account on Binos or accessing binos-ai.com, you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use the platform.

These terms apply to all users of Binos including VC fund professionals, founders submitting pitch decks, and any other visitors to the platform. We reserve the right to update these terms at any time and will notify users of material changes via email.`,
  },
  {
    title: "Account Access and Approval",
    content: `Access to the Binos dashboard requires approval from the Binos team. By signing up, you are submitting an application for access. We reserve the right to approve or reject any application at our sole discretion.

You are responsible for maintaining the confidentiality of your account credentials. You may not share your account with others. You must notify us immediately at anas@binos-ai.com if you suspect unauthorised access to your account.

We reserve the right to suspend or terminate any account that we believe is being used in violation of these terms.`,
  },
  {
    title: "Permitted Use",
    content: `Binos is a software platform for venture capital professionals. You may use Binos to analyse pitch decks, track portfolio companies, build investment memos, conduct market research, and manage your fund's deal flow.

You may not use Binos for any unlawful purpose, to harm or defraud others, to transmit malicious code or malware, to attempt to gain unauthorised access to other users' data, to reverse engineer or attempt to extract the source code of the platform, or to resell or commercially redistribute access to the platform without our written consent.`,
  },
  {
    title: "Your Data and Content",
    content: `You retain full ownership of all data, content, and intellectual property you upload or create on Binos including portfolio company information, investment memos, notes, and any other content.

By using Binos, you grant us a limited, non-exclusive licence to store, process, and display your content solely for the purpose of providing the platform to you. We do not claim any ownership over your content.

You are responsible for ensuring you have the right to upload and process any content you submit to Binos, including pitch decks submitted by founders on your behalf.`,
  },
  {
    title: "Pitch Deck Submissions",
    content: `By submitting a pitch deck through binos-ai.com/founders, you confirm that you are authorised to share the deck and grant Binos a limited, non-exclusive licence to store and share the deck with verified VC funds on the platform whose investment thesis matches your startup.

Binos will not share your pitch deck publicly, sell it, or use it for any purpose beyond matching it with relevant VC funds. You may request deletion of your pitch deck at any time by emailing anas@binos-ai.com.

Binos does not guarantee that any VC fund will review, respond to, or invest in any startup that submits a pitch deck through the platform.`,
  },
  {
    title: "AI-Generated Content",
    content: `Binos uses AI to assist with pitch deck analysis, investment memo generation, thesis matching, and market research. All AI-generated content is provided for informational purposes only and does not constitute investment advice, financial advice, or legal advice.

You are solely responsible for any investment decisions you make based on content generated by Binos. We do not guarantee the accuracy, completeness, or suitability of any AI-generated output. Always conduct your own due diligence.`,
  },
  {
    title: "Subscription and Payment",
    content: `Access to Binos is provided on a subscription basis. Subscription fees are charged monthly in advance. We reserve the right to change pricing with 30 days notice to existing subscribers.

Early access pricing locked in at the time of subscription will be honoured for the lifetime of the subscription provided it remains active. If a subscription lapses, founding pricing cannot be reinstated.

Refunds are available within 7 days of the initial subscription payment if you are not satisfied with the platform.`,
  },
  {
    title: "Intellectual Property",
    content: `The Binos platform, including its design, code, features, and branding, is the intellectual property of Binos and is protected by applicable intellectual property laws. You may not copy, modify, distribute, or create derivative works from any part of the platform without our written consent.

The name Binos, the Binos logo, and Venture Intelligence are trademarks of Binos. You may not use these marks without our written consent.`,
  },
  {
    title: "Limitation of Liability",
    content: `Binos is provided on an as-is basis. We make no warranties, express or implied, regarding the reliability, accuracy, or suitability of the platform for any particular purpose.

To the maximum extent permitted by law, Binos shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the platform, including but not limited to loss of data, loss of profits, or any investment decisions made based on content generated by the platform.

Our total liability to you for any claim arising from your use of Binos shall not exceed the amount you paid us in the three months preceding the claim.`,
  },
  {
    title: "Governing Law",
    content: `These terms are governed by the laws of India. Any disputes arising from these terms or your use of Binos shall be subject to the exclusive jurisdiction of the courts of Mumbai, Maharashtra.

If any provision of these terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.

These terms constitute the entire agreement between you and Binos regarding your use of the platform and supersede all prior agreements.`,
  },
  {
    title: "Contact",
    content: `For any questions regarding these terms, contact us at anas@binos-ai.com. We will respond to all inquiries within 72 hours.

These terms were last updated in May 2025.`,
  },
];

function Section({ title, content, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.border}`, overflow: "hidden" }}>
      <button onClick={() => setOpen(!open)}
        style={{ width: "100%", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: T.red, letterSpacing: "0.1em", minWidth: 28 }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.white }}>{title}</span>
        </div>
        <span style={{ fontSize: 18, color: T.faint, transform: open ? "rotate(45deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}>+</span>
      </button>
      {open && (
        <div style={{ padding: "0 24px 20px 68px" }}>
          {content.split("\n\n").map((para, i) => (
            <p key={i} style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.8, margin: "0 0 12px", fontFamily: T.font }}>{para}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PrivacyAndTerms() {
  const [page, setPage] = useState("privacy");

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.font }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 48px", borderBottom: `1px solid ${T.border}`, position: "sticky", top: 0, background: "rgba(10,10,10,0.95)", zIndex: 100, backdropFilter: "blur(20px)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={logo} alt="Binos" style={{ width: 32, height: 32, borderRadius: 8 }} />
          <span style={{ fontSize: 16, fontWeight: 900, color: T.white, letterSpacing: "-0.3px" }}>Binos</span>
        </div>
        <a href="/" style={{ fontSize: 12, color: T.muted, textDecoration: "none", fontWeight: 500 }}>← Back to home</a>
      </nav>

      {/* Hero */}
      <div style={{ padding: "64px 48px 40px", maxWidth: 800, margin: "0 auto" }}>
        <p style={{ fontSize: 10, fontWeight: 800, color: T.red, letterSpacing: "0.2em", textTransform: "uppercase", margin: "0 0 12px" }}>Legal</p>
        <h1 style={{ fontFamily: T.serif, fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 400, color: T.white, letterSpacing: "-2px", lineHeight: 1.05, margin: "0 0 14px" }}>
          {page === "privacy" ? "Privacy Policy." : "Terms of Service."}
        </h1>
        <p style={{ fontSize: 14, color: T.muted, margin: "0 0 32px", lineHeight: 1.7 }}>
          {page === "privacy"
            ? "How Binos collects, uses, and protects your data. We take this seriously."
            : "The rules governing your use of the Binos platform. Plain language, no surprises."}
        </p>

        {/* Toggle */}
        <div style={{ display: "inline-flex", background: "#161616", border: `1px solid ${T.border}`, borderRadius: 10, padding: 4, gap: 4 }}>
          {["privacy", "terms"].map(p => (
            <button key={p} onClick={() => setPage(p)}
              style={{ padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: T.font, fontSize: 13, fontWeight: 700, transition: "all 0.2s", background: page === p ? T.red : "transparent", color: page === p ? T.white : T.muted }}>
              {p === "privacy" ? "Privacy Policy" : "Terms of Service"}
            </button>
          ))}
        </div>

        {/* Last updated */}
        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", margin: "20px 0 0", fontWeight: 500 }}>Last updated: May 2025 · Questions? anas@binos-ai.com</p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 48px 80px" }}>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
          {(page === "privacy" ? PRIVACY_SECTIONS : TERMS_SECTIONS).map((section, i) => (
            <Section key={i} title={section.title} content={section.content} index={i} />
          ))}
        </div>

        {/* Footer note */}
        <div style={{ marginTop: 24, padding: "20px 24px", background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.15)", borderRadius: 12 }}>
          <p style={{ fontSize: 12, color: T.muted, margin: 0, lineHeight: 1.7 }}>
            <span style={{ color: T.white, fontWeight: 700 }}>Questions about this policy?</span> We're a small team and we read every email. Reach us at{" "}
            <a href="mailto:anas@binos-ai.com" style={{ color: T.red, textDecoration: "none", fontWeight: 600 }}>anas@binos-ai.com</a>
            {" "}and we'll respond within 72 hours.
          </p>
        </div>
      </div>
    </div>
  );
}
